import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProposalContext, publicFileUrl } from "@/lib/proposalContext";

/**
 * Load a private file (anything under `/files/{path}`) through axios so
 * the JWT-refresh interceptor handles token expiry transparently — then
 * expose the bytes as a same-origin `blob:` URL that `<img>` (or any
 * other browser primitive) can render reliably.
 *
 * Why not just `<img src={imageSrc(path)}>`? That URL embeds a token
 * via `?auth=` query param read from localStorage. When the token
 * expires, the browser shows a broken image because raw `<img>` requests
 * can't piggy-back on axios's silent refresh-and-retry flow. Going
 * through axios fixes that — and also lets us show a clean fallback if
 * the file genuinely doesn't exist.
 *
 * Returns `{ src, error, loading }`:
 *   - `src` is a `blob:` URL ready for `<img src={...}>`, or `null`
 *     while still loading / on error.
 *   - `error` is the axios error, or `null`.
 *   - `loading` is `true` until the fetch settles.
 */

/* ---------- Process-wide blob cache --------------------------------------
 *
 * Without this, every `<img>` that mounts pays for its own /files/{path}
 * roundtrip. On a flyer page the same render gets requested by the hero
 * crop, the side thumbnail, AND the print template at the same time —
 * three downloads, three blob URLs, three sets of bytes in memory.
 *
 * The cache is keyed on path → ``{ promise, blobUrl, refcount }``:
 *   - promise:   the in-flight axios call (if any), so concurrent mounts
 *                share one request instead of racing.
 *   - blobUrl:   the resolved blob URL (set once the promise resolves).
 *   - refcount:  number of live consumers holding a reference. When the
 *                last consumer unmounts the URL is revoked + the entry
 *                is dropped, freeing the underlying bytes.
 *
 * The cache lives on ``window`` so hot-reload doesn't wipe it between
 * edits (development comfort — in production it just lives in the
 * module). It is intentionally NOT persistent across navigations to
 * different SPA pages because React's strict-mode double-mount + the
 * refcount drain handle that naturally.
 *
 * Failures (404, 500) are NOT cached — a transient error must be
 * retryable on the next mount. */
const _imageCache = (typeof window !== "undefined" && (window.__sosImgCache__ ||= new Map())) || new Map();

function acquire(path) {
  let entry = _imageCache.get(path);
  if (!entry) {
    entry = {
      promise: api
        .get(`/files/${path}`, { responseType: "blob" })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          // Mutate the same entry so subscribers all see the resolved URL.
          const live = _imageCache.get(path);
          if (live) live.blobUrl = url;
          return url;
        })
        .catch((err) => {
          // Drop failed entries so the next mount can retry.
          _imageCache.delete(path);
          throw err;
        }),
      blobUrl: null,
      refcount: 0,
    };
    _imageCache.set(path, entry);
  }
  entry.refcount += 1;
  return entry;
}

function release(path) {
  const entry = _imageCache.get(path);
  if (!entry) return;
  entry.refcount -= 1;
  if (entry.refcount > 0) return;
  if (entry.blobUrl) {
    try {
      URL.revokeObjectURL(entry.blobUrl);
    } catch (_) {
      /* noop — already revoked */
    }
  }
  _imageCache.delete(path);
}

export function useAuthedImage(path) {
  const { mode, token } = useProposalContext();
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(Boolean(path));

  useEffect(() => {
    if (!path) {
      setSrc(null);
      setError(null);
      setLoading(false);
      return undefined;
    }
    // Public / shared proposal — the anonymous file endpoint returns
    // the bytes directly, no auth needed, so a plain URL works without
    // any blob-fetch dance. Cache-Control on the backend makes repeat
    // views essentially free.
    if (mode === "public" && token) {
      setSrc(publicFileUrl(token, path));
      setError(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const entry = acquire(path);
    // Fast-path: another consumer has already resolved the bytes for this
    // path, so we can hand the cached blob URL back synchronously on the
    // NEXT effect tick without re-hitting the backend.
    if (entry.blobUrl) {
      setSrc(entry.blobUrl);
      setLoading(false);
    } else {
      entry.promise
        .then((url) => {
          if (cancelled) return;
          setSrc(url);
        })
        .catch((e) => {
          if (cancelled) return;
          setSrc(null);
          setError(e);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }
    return () => {
      cancelled = true;
      release(path);
    };
  }, [path, mode, token]);

  return { src, error, loading };
}
